/* istanbul ignore file */
import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { isSparePartBasketItem } from '@ecs-services/basket.service';
import {
    DriveGroup,
    GearOilSparePart,
    PlantsWithSpareParts,
    SparePartService,
} from '@ecs-services/spare-part/spare-part.service';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { OnDestroyBase } from '@os-components';
import { SewHeadline, SewRichtext, SewSkeleton, SewTag } from '@sew/ui-components-angular/standalone';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PartRow } from '../../../../part-table/part-table.interface';
import { InjectionDataStep, StepType, WIZARD_STEP_TOKEN } from '../../part-configuration-wizard.component';
import { BasketItemRow, StoreStepItems } from '../../part-configuration-wizard.service';
import { CrossSellingTableComponent } from '../cross-selling-table/cross-selling-table.component';
import { CrossSellingPartData } from '../cross-selling-table/cross-selling-table.interface';

export interface GearOilConfigurationData {
    type: StepType.GEAR_OIL;
    parts: GearOilSparePart[];
    items: Observable<CrossSellingPartData[]>;
    isLoggedIn: boolean;
}
export interface GearOilTableData {
    uniqueContextSparePartId: string;
    driveGroup?: DriveGroup;
    spareParts: BasketItemRow[];
}

@Component({
    selector: 'ecs-gear-oil-configuration',
    templateUrl: './gear-oil-configuration.component.html',
    styleUrls: ['./gear-oil-configuration.component.scss'],
    imports: [
        CommonModule,
        TranslateDirective,
        CrossSellingTableComponent,
        TranslatePipe,
        SewTag,
        SewHeadline,
        SewRichtext,
        SewSkeleton,
    ],
})
export class GearOilConfigurationComponent extends OnDestroyBase implements OnDestroy {
    public isLoggedIn: boolean;
    public items?: CrossSellingPartData[];

    public storedRowsPerTable: { [sparePartId: string]: PartRow[] } = {};

    @ViewChildren(CrossSellingTableComponent)
    private crossSellingTables: QueryList<CrossSellingTableComponent> = new QueryList();

    private stepItems: StoreStepItems = {};
    private allSelectedItems: GearOilTableData[] = [];

    public constructor(
        @Inject(WIZARD_STEP_TOKEN)
        private injection: InjectionDataStep<GearOilConfigurationData>,
        private sparePartService: SparePartService
    ) {
        super();
        const data = this.injection.stepData;
        this.isLoggedIn = data.isLoggedIn;
        this.injection.valid.next(false);

        this.injection.nextStep.pipe(takeUntil(this.destroySubject)).subscribe(() => this.emitOutput());

        data.items.pipe(takeUntil(this.destroySubject)).subscribe((items) => {
            this.items = items;
            this.getAvailabilityAndMergeIntoItems(this.extractPlantsWithSpareParts());
        });

        this.restoreSavedData();
    }

    public onRowSelect(uniqueContextSparePartId: string, rows: BasketItemRow[]): void {
        this.updateSelectedItems(uniqueContextSparePartId, rows);
        this.checkIsStepValid();
    }

    // Required due to Master Data sometimes being wrong and returning KG instead of L for gear oil
    // Not recommended by DEVs as data should be fixed not worked around
    // see: https://brudetfs.de.eu.sew/tfs/DefaultCollection/IT_PublicALM/_workitems/edit/715731
    public adjustGearOilUnit(item: CrossSellingPartData): CrossSellingPartData {
        if (!!item?.context?.gearOilQuantity && !!item?.context?.gearOilUnit) {
            item.context.gearOilUnit = item.context.gearOilUnit === 'KG' ? 'L' : item.context.gearOilUnit;
        }

        return item;
    }

    private restoreSavedData() {
        const storedData = this.injection.storedData;
        if (storedData) {
            this.injection.stepData.parts.forEach((sparePart) => {
                const specId = sparePart.crossSelling?.gearOil?.specificationId;
                if (!specId) {
                    return;
                }
                const storedStepItems = storedData[specId];
                if (storedStepItems) {
                    let storedRows: PartRow[] = [];
                    storedStepItems.forEach((basketItemRow) => {
                        if (basketItemRow.row) {
                            storedRows = [...storedRows, basketItemRow.row];
                        }
                    });
                    this.storedRowsPerTable[specId] = storedRows;
                }
            });
        }
    }

    private emitOutput() {
        this.getFinalAmount();

        this.allSelectedItems.forEach((table) => {
            this.stepItems[table.uniqueContextSparePartId] = table.spareParts;
            this.stepItems[table.uniqueContextSparePartId]
                .map(({ basketItem }) => basketItem)
                .filter(isSparePartBasketItem)
                .forEach(
                    (basketItem) =>
                        // all parts should be equal concerning the isIndustrialGear flag
                        // as we don't have a direct reference to the selected spare part, we use simply position 0
                        (basketItem.isPartOfIndustrialGear =
                            this.injection.stepData.parts[0].isIndustrialGear)
                );
        });
        this.injection.addBasketItems(this.stepItems);
    }

    private getFinalAmount() {
        this.crossSellingTables.forEach((crossSellingTable) => {
            this.updateSelectedItems(
                crossSellingTable.uniqueContextSparePartId,
                crossSellingTable.createBasketItems()
            );
        });
    }

    private updateSelectedItems(uniqueContextSparePartId: string, rows: BasketItemRow[]) {
        const remainingItems = this.allSelectedItems.filter(
            (table) => table.uniqueContextSparePartId !== uniqueContextSparePartId
        );

        if (rows.length > 0) {
            remainingItems.push({ uniqueContextSparePartId, spareParts: rows });
        }

        this.allSelectedItems = remainingItems;
    }

    private checkIsStepValid() {
        if (this.allSelectedItems.length > 0) {
            this.injection.valid.next(true);
        } else {
            this.injection.valid.next(false);
        }
    }

    private extractPlantsWithSpareParts() {
        let plantsWithSpareParts: PlantsWithSpareParts = [];

        this.items?.forEach((sparePartRow) => {
            sparePartRow.parts.forEach((part) => {
                const existing = plantsWithSpareParts.find(
                    (plantWithSparePart) => plantWithSparePart.plantId === part.rowData.plantId
                );
                if (existing) {
                    existing.spareParts = [
                        ...existing.spareParts,
                        { id: part.rowData.id, availabilityCode: part.rowData.availabilityCode },
                    ];
                } else {
                    plantsWithSpareParts = [
                        ...plantsWithSpareParts,
                        {
                            plantId: part.rowData.plantId,
                            spareParts: [
                                {
                                    id: part.rowData.id,
                                    availabilityCode: part.rowData.availabilityCode,
                                },
                            ],
                        },
                    ];
                }
            });
        });

        return plantsWithSpareParts;
    }

    private getAvailabilityAndMergeIntoItems(plantsWithSpareParts: PlantsWithSpareParts) {
        this.sparePartService
            .getSparePartsAvailability(plantsWithSpareParts)
            .pipe(takeUntil(this.destroySubject))
            .subscribe((sparePartsWithAvailability) => {
                this.items?.forEach((partTable) =>
                    partTable.parts.forEach((part) => {
                        part.rowData.availability = sparePartsWithAvailability.find(
                            (sparePartWithAvailability) =>
                                sparePartWithAvailability.sparePartId === part.rowData.id
                        )?.availability;
                    })
                );
                this.items?.forEach((partTable) => (partTable.parts = [...partTable.parts]));
            });
    }
}
